import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import {
  localNerisStatusMatching,
  statusMatching,
} from '../../../pages/Incidents/utils/statusMatching';

/**
 * Printable table of every incident matching the current filters.
 *
 * Same portal approach as IncidentPrintView: rendered as a sibling of #root so
 * it survives the `#root { display: none }` print rule. While a list print is
 * in progress, Incidents.jsx adds the `print-incidents-list` class to <body>,
 * which swaps the single-incident report out for this table.
 */

export const LIST_PRINT_BODY_CLASS = 'print-incidents-list';

const formatTrt = (value) =>
  value ? new Date(value * 1000).toISOString().substr(11, 8) : '—';

export default function IncidentsListPrintView({
  rows,
  department,
  filtersSummary,
  callStatus,
}) {
  const [portalEl, setPortalEl] = useState(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = 'incidents-list-print-view';
    document.body.appendChild(el);
    setPortalEl(el);
    return () => {
      if (document.body.contains(el)) document.body.removeChild(el);
    };
  }, []);

  if (!portalEl || !rows) return null;

  const callStatusLabel = (value) =>
    callStatus?.find((item) => item.value === value)?.label || value || '—';

  return createPortal(
    <>
      <style>{`
        #incidents-list-print-view { display: none; }
        @media print {
          @page { size: landscape; margin: 10mm; }
          body.${LIST_PRINT_BODY_CLASS} #root,
          body.${LIST_PRINT_BODY_CLASS} #incident-print-view { display: none !important; }
          body.${LIST_PRINT_BODY_CLASS} #incidents-list-print-view { display: block !important; }
          #incidents-list-print-view thead { display: table-header-group; }
          #incidents-list-print-view tr { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>
              {(department || 'Fire Department').replace(/\s*Local\s*\d+/gi, '').trim()}
            </div>
            <div style={styles.subtitle}>Incident Report</div>
          </div>
          <div style={styles.meta}>
            <div>Generated: {moment().format('MMM D, YYYY HH:mm')}</div>
            <div>Total incidents: {rows.length}</div>
          </div>
        </div>

        {filtersSummary.length > 0 && (
          <div style={styles.filters}>
            {filtersSummary.map(({ label, value }) => (
              <span key={label} style={styles.filterItem}>
                <strong>{label}:</strong> {value}
              </span>
            ))}
          </div>
        )}

        <table style={styles.table}>
          <thead>
            <tr>
              {['#', 'Dispatch Time', 'Number', 'Type', 'Address', 'Status', 'Units', 'OIC', 'Local', 'USFA', 'TRT'].map(
                (h) => (
                  <th key={h} style={styles.th}>{h}</th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.incident_id} style={idx % 2 ? styles.rowAlt : undefined}>
                <td style={styles.td}>{idx + 1}</td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  {row.dispatch_time
                    ? moment(row.dispatch_time).utc().format('MMM D, YYYY HH:mm')
                    : '—'}
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{row.incident_number || '—'}</td>
                <td style={styles.td}>{row.incident_type || row.cad_type || '—'}</td>
                <td style={styles.td}>{row.address || '—'}</td>
                <td style={styles.td}>{row.status ? callStatusLabel(row.status) : '—'}</td>
                <td style={styles.td}>{row.responded_units || row.first_arriving_unit || '—'}</td>
                <td style={styles.td}>
                  {row.name ? `${row.name}${row.rank ? ` (${row.rank})` : ''}` : '—'}
                </td>
                <td style={styles.td}>{localNerisStatusMatching(row.form_status).text}</td>
                <td style={styles.td}>{statusMatching(row.neris_status).text}</td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formatTrt(row.trt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>,
    portalEl
  );
}

const styles = {
  page: {
    fontFamily: 'Arial, sans-serif',
    fontSize: 10,
    color: '#111',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    borderBottom: '2px solid #BF1D2D',
    paddingBottom: 6,
    marginBottom: 8,
  },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { fontSize: 12, color: '#444' },
  meta: { textAlign: 'right', fontSize: 10, color: '#444' },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 16px',
    fontSize: 10,
    marginBottom: 8,
  },
  filterItem: { whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '4px 6px',
    borderBottom: '1px solid #999',
    background: '#f0f0f0',
    fontSize: 9,
    textTransform: 'uppercase',
  },
  td: {
    padding: '4px 6px',
    borderBottom: '1px solid #ddd',
    verticalAlign: 'top',
  },
  rowAlt: { background: '#fafafa' },
};
